import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState, type MouseEvent } from 'react'
import { ApiError } from '../../api/client'
import {
  adamModuleQuery,
  adamModuleTableQuery,
  adamModulesShellQuery,
  createAdamModule,
  removeAdamModule,
  saveAdamModule,
  type AdamModuleForm,
} from '../../api/settingsEaseedeskAdamIntopModules'
import { DataTable, useTableState, type TableColumn } from '../../ui/DataTable'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'
import { esc, PythonFilesHierarchyPanel } from './AdamIntopPythonFilesPage'

/**
 * Settings → Easeedesk → Adam Intop → Extended modules, intreg: citire SI
 * scriere.
 *
 * Element cu element fata de `version-3/easeedesk_settings/adam_intop/
 * extended_modules.php` + `edit_extended_module.php`, coloana cu coloana fata
 * de `AdamIntopExtendedModulesModel` din
 * `public/js/settings/easeedesk/adam_intop_settings.js`.
 *
 * CELE DOUA FILE DIN RANDUL DESFASURAT
 * ------------------------------------
 *
 * In portal, `+` pe un rand aducea prin ajax `extended_module_tabs` - o macheta
 * cu doua legaturi, "Edit Extended Module" si "Files Hierarchy" - si fiecare
 * dintre ele mai facea o cerere. Aici randul desfasurat are chiar cele doua
 * file, si fiecare isi cere singura datele.
 */

const TABLE_ID = 'adam-intop-extended-modules'

/** `AdamIntopExtendedModulesModel`: aceleasi sapte coloane, in aceeasi ordine. */
export function adamModuleColumns(): TableColumn[] {
  const text = (key: string): TableColumn['html'] => (row) => esc(String(row[key] ?? ''))
  return [
    {
      index: 0,
      title: '',
      orderable: false,
      visible: true,
      width: 30,
      html: (row) =>
        `<center class="show_details color-dark-grey pointer font-16" data-id="${Number(row.ID)}" data-type=""><i class="fas fa-plus-circle"></i></center>`,
    },
    { index: 1, title: 'ID', orderable: true, visible: true, width: 30, className: 'a-left', html: text('ID') },
    { index: 2, title: 'Module Name', orderable: true, visible: true, className: 'a-left', html: text('module_name') },
    {
      index: 3,
      title: 'Eligible for Python',
      orderable: true,
      visible: true,
      className: 'a-left',
      html: text('eligible_for_python'),
    },
    {
      index: 4,
      title: 'Active in Adam Intop',
      orderable: true,
      visible: true,
      className: 'a-left',
      html: text('active_in_adam_intop'),
    },
    { index: 5, title: 'Status', orderable: true, visible: true, className: 'a-left', html: text('status') },
    {
      index: 6,
      title: 'Actions',
      orderable: false,
      visible: true,
      width: 130,
      html: (row) =>
        `<a href="javascript:void(0)" data-id="${Number(row.ID)}" class="btn btn-xs btn-danger module btn-delete-module pull-left" style="margin-left:6px"> <i class="fas fa-times" aria-hidden="true"></i> Delete </a>`,
    },
  ]
}

/** Coloana SQL a fiecarui indice, ca in `getExtendedModulesData()`. */
export const ORDER_NAMES = ['ID', 'ID', 'module_name', 'eligible_for_python', 'active_in_adam_intop', 'status', 'ID']

export const EMPTY_FORM: AdamModuleForm = {
  module_name: '',
  eligible_for_python: '',
  active_in_adam_intop: '',
  status: '',
}

export function AdamIntopExtendedModulesPage() {
  const queryClient = useQueryClient()
  const shell = useQuery(adamModulesShellQuery())
  /* `order: [[1, 'desc']]` - pe ce si cum porneste lista veche. */
  const [state, setState] = useTableState(TABLE_ID, {
    start: 0,
    length: 25,
    order: { column: 1, dir: 'desc' },
    search: '',
  })
  const [creating, setCreating] = useState(false)
  const columns = useMemo(() => adamModuleColumns(), [])
  const table = useQuery({
    ...adamModuleTableQuery({
      draw: 1,
      start: state.start,
      length: state.length,
      orderColumn: state.order.column,
      orderName: ORDER_NAMES[state.order.column] ?? 'ID',
      orderDir: state.order.dir,
      search: state.search,
    }),
    placeholderData: keepPreviousData,
  })

  const redraw = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'adam_intop', 'extended_modules'] })
  }

  const remove = useMutation({
    mutationFn: (module: number) => removeAdamModule(module),
    onSuccess: () => {
      toast('Module removed.', 'Success')
      redraw()
    },
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  if (shell.error instanceof ApiError && shell.error.status === 403) return <Forbidden />
  if (!shell.data) return <Spinner />

  const rows = (table.data?.data ?? []) as unknown as Record<string, unknown>[]

  const onClick = (event: MouseEvent<HTMLDivElement>) => {
    const kill = (event.target as HTMLElement).closest('.btn-delete-module')
    if (kill instanceof HTMLElement) {
      event.preventDefault()
      /* Intrebarea din swal, cu acelasi text. */
      if (!window.confirm('Are you sure you want to delete this Module?\nMissing data may cause serious problems.')) return
      remove.mutate(Number(kill.dataset.id ?? 0))
    }
  }

  return (
    <div onClick={onClick}>
      <button type="button" className="pull-right btn btn-success btn-sm" onClick={() => setCreating(true)}>
        <i className="fas fa-plus" aria-hidden="true"></i> Create Extended Module
      </button>
      <h4>
        <strong>Extended</strong> Modules
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage, create or edit <strong>Adam Intop extended modules</strong>
      </label>
      <hr />
      <DataTable
        id={TABLE_ID}
        columns={columns}
        rows={rows}
        total={table.data?.recordsFiltered ?? 0}
        fetching={table.isFetching}
        error={table.error ? table.error.message : null}
        onRetry={() => void table.refetch()}
        state={state}
        onState={setState}
        rowId={(row) => String(row.DT_RowId ?? `row_${Number(row.ID)}`)}
        emptyHint="An extended module groups the python files Adam Intop can run."
        details={(row) => <ModulePanel module={Number(row.ID)} onSaved={redraw} />}
      />
      <NewModuleModal open={creating} onClose={() => setCreating(false)} onSaved={redraw} />
    </div>
  )
}

function Forbidden() {
  return (
    <div className="alert alert-danger">
      <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
    </div>
  )
}

/** Randul desfasurat: cele doua file pe care le dadea `extended_module_tabs`. */
export function ModulePanel({ module, onSaved }: { module: number; onSaved(): void }) {
  const [tab, setTab] = useState<'edit' | 'files'>('edit')
  return (
    <div style={{ padding: '0px 15px' }}>
      <ul className="nav nav-tabs managementList" role="tablist">
        <li className={tab === 'edit' ? 'active' : undefined}>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault()
              setTab('edit')
            }}
          >
            <i className="fas fa-pencil-alt" aria-hidden="true"></i> Edit Extended Module
          </a>
        </li>
        <li className={tab === 'files' ? 'active' : undefined}>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault()
              setTab('files')
            }}
          >
            <i className="fas fa-filter" aria-hidden="true"></i> Files Hierarchy
          </a>
        </li>
      </ul>
      <div className="tab-content">
        {tab === 'edit' ? (
          <EditModulePanel module={module} onSaved={onSaved} />
        ) : (
          <PythonFilesHierarchyPanel module={module} />
        )}
      </div>
    </div>
  )
}

/** `edit_extended_module.php`, cu formularul lui `edit_extended_module_save`. */
export function EditModulePanel({ module, onSaved }: { module: number; onSaved(): void }) {
  const query = useQuery(adamModuleQuery(module))
  const [form, setForm] = useState<AdamModuleForm>(EMPTY_FORM)

  useEffect(() => {
    const data = query.data
    if (!data) return
    setForm({
      module_name: data.module.module_name,
      eligible_for_python: String(data.module.eligible_for_python ?? ''),
      active_in_adam_intop: String(data.module.active_in_adam_intop ?? ''),
      status: String(data.module.status ?? ''),
    })
  }, [query.data])

  const save = useMutation({
    mutationFn: () => saveAdamModule(module, form),
    onSuccess: () => {
      toast('Module saved.', 'Success')
      onSaved()
    },
    onError: (error) => toast(error instanceof ApiError ? error.message : 'Something went wrong.', 'Error', 'error'),
  })

  if (query.error instanceof ApiError && query.error.status === 403) return <Forbidden />
  if (!query.data) return <Spinner />

  return (
    <form
      method="post"
      style={{ paddingTop: 15 }}
      onSubmit={(e) => {
        e.preventDefault()
        save.mutate()
      }}
    >
      <ModuleFields idPrefix={`edit-${module}`} form={form} onChange={setForm} />
      <div className="form-group mt-2 text-center center-block">
        <button type="submit" className="btn btn-primary" disabled={!complete(form) || save.isPending}>
          Save
        </button>
      </div>
    </form>
  )
}

/** `newResource` din `extended_modules.php`, cu `create_extended_module_save`. */
export function NewModuleModal({ open, onClose, onSaved }: { open: boolean; onClose(): void; onSaved(): void }) {
  const [form, setForm] = useState<AdamModuleForm>(EMPTY_FORM)

  useEffect(() => {
    if (open) setForm(EMPTY_FORM)
  }, [open])

  const create = useMutation({
    mutationFn: () => createAdamModule(form),
    onSuccess: () => {
      toast('Module created.', 'Success')
      onClose()
      onSaved()
    },
    onError: (error) => toast(error instanceof ApiError ? error.message : 'Something went wrong.', 'Error', 'error'),
  })

  return (
    <Modal
      id="newExtendedModule"
      labelledBy="newExtendedModuleLabel"
      open={open}
      onClose={onClose}
      title="Add New Extended Module"
    >
      <form
        method="post"
        onSubmit={(e) => {
          e.preventDefault()
          if (!complete(form)) return
          create.mutate()
        }}
      >
        <ModuleFields idPrefix="new" form={form} onChange={setForm} />
        <div className="form-group aright">
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>{' '}
          <button type="submit" className="btn btn-primary" disabled={!complete(form) || create.isPending}>
            Save
          </button>
        </div>
      </form>
    </Modal>
  )
}

/** Cele patru campuri cerute de `form_validation` in amandoua salvarile. */
function complete(form: AdamModuleForm): boolean {
  return (
    form.module_name.trim() !== '' &&
    form.eligible_for_python !== '' &&
    form.active_in_adam_intop !== '' &&
    form.status !== ''
  )
}

function ModuleFields({
  idPrefix,
  form,
  onChange,
}: {
  idPrefix: string
  form: AdamModuleForm
  onChange(next: AdamModuleForm): void
}) {
  const yesNo = (key: keyof AdamModuleForm, label: string) => (
    <div className="col-md-6" key={key}>
      <div className="form-group">
        <label htmlFor={`${idPrefix}-${key}`} className="control-label">
          {label} <span className="required">*</span>
        </label>
        <select
          id={`${idPrefix}-${key}`}
          className="form-control input-sm"
          name={key}
          value={form[key]}
          onChange={(e) => onChange({ ...form, [key]: e.target.value })}
        >
          <option value=""> - select - </option>
          <option value="1">Yes</option>
          <option value="0">No</option>
        </select>
      </div>
    </div>
  )

  return (
    <div className="row">
      <div className="col-md-6">
        <div className="form-group">
          <label htmlFor={`${idPrefix}-module_name`} className="control-label">
            Module Name <span className="required">*</span>
          </label>
          <input
            id={`${idPrefix}-module_name`}
            type="text"
            className="form-control input-sm"
            name="module_name"
            value={form.module_name}
            onChange={(e) => onChange({ ...form, module_name: e.target.value })}
          />
        </div>
      </div>
      {yesNo('eligible_for_python', 'Eligible for Python')}
      {yesNo('active_in_adam_intop', 'Active in Adam Intop')}
      {yesNo('status', 'Status')}
    </div>
  )
}
